import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  AlertTriangle, TrendingUp, TrendingDown, Clock, CheckCircle2, 
  XCircle, AlertCircle, BarChart3, PieChart, ArrowRight, RefreshCw,
  Calendar, Users, Building2, FileWarning
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { formatDate } from '@/utils/formatters';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RechartsPie, Pie, Cell, LineChart, Line, Legend
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Colors
const SEVERITY_COLORS = {
  critical: '#ef4444',
  medium: '#f97316',
  low: '#eab308'
};

const CHART_COLORS = ['#06b6d4', '#8b5cf6', '#22c55e', '#f59e0b', '#ef4444', '#6366f1', '#ec4899', '#14b8a6'];

const NcrDashboard = ({ onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/ncrs/dashboard/advanced`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching NCR dashboard:', error);
      toast.error('Failed to load NCR dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDashboardData();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-red-400" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-8 text-slate-400">
        Failed to load dashboard data
      </div>
    );
  }

  // Prepare chart data
  const severityData = [
    { name: 'Critical', value: data.open_by_severity.critical, color: SEVERITY_COLORS.critical },
    { name: 'Medium', value: data.open_by_severity.medium, color: SEVERITY_COLORS.medium },
    { name: 'Low', value: data.open_by_severity.low, color: SEVERITY_COLORS.low }
  ].filter(d => d.value > 0);

  const departmentData = Object.entries(data.by_department)
    .map(([dept, stats]) => ({
      name: dept,
      open: stats.open,
      closed: stats.closed,
      critical: stats.critical
    }))
    .sort((a, b) => (b.open + b.closed) - (a.open + a.closed))
    .slice(0, 8);

  const sourceData = Object.entries(data.by_source)
    .map(([source, count], idx) => ({
      name: source,
      value: count,
      color: CHART_COLORS[idx % CHART_COLORS.length]
    }));

  return (
    <div className="space-y-6" data-testid="ncr-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-red-500/20 border border-red-500/30 flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-red-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white uppercase tracking-wide">NCR Dashboard</h2>
            <p className="text-xs text-slate-500">Non-Conformance Analytics</p>
          </div>
        </div>
        <Button
          onClick={handleRefresh}
          disabled={refreshing}
          variant="outline"
          size="sm"
          className="border-slate-700"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {/* Total Open */}
        <div className="bg-slate-900/60 border border-red-500/20 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            <span className="text-xs text-slate-400 uppercase">Open NCRs</span>
          </div>
          <p className="text-2xl font-bold text-white">{data.summary.total_open}</p>
        </div>

        {/* Critical */}
        <div className="bg-slate-900/60 border border-red-600/30 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <XCircle className="w-4 h-4 text-red-500" />
            <span className="text-xs text-slate-400 uppercase">Critical</span>
          </div>
          <p className="text-2xl font-bold text-red-400">{data.open_by_severity.critical}</p>
        </div>

        {/* Overdue */}
        <div className="bg-slate-900/60 border border-orange-500/30 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-orange-400" />
            <span className="text-xs text-slate-400 uppercase">Overdue</span>
          </div>
          <p className="text-2xl font-bold text-orange-400">{data.overdue_count}</p>
        </div>

        {/* Closed This Month */}
        <div className="bg-slate-900/60 border border-green-500/20 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-4 h-4 text-green-400" />
            <span className="text-xs text-slate-400 uppercase">Total Closed</span>
          </div>
          <p className="text-2xl font-bold text-green-400">{data.summary.total_closed}</p>
        </div>

        {/* Avg Close Time */}
        <div className="bg-slate-900/60 border border-cyan-500/20 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-cyan-400" />
            <span className="text-xs text-slate-400 uppercase">Avg Close</span>
          </div>
          <p className="text-2xl font-bold text-cyan-400">{data.summary.avg_close_time_days}<span className="text-sm text-slate-500 ml-1">days</span></p>
        </div>

        {/* Internal vs External */}
        <div className="bg-slate-900/60 border border-purple-500/20 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <FileWarning className="w-4 h-4 text-purple-400" />
            <span className="text-xs text-slate-400 uppercase">Int / Ext</span>
          </div>
          <p className="text-2xl font-bold text-purple-400">
            {data.summary.internal_count}<span className="text-slate-500 mx-1">/</span>{data.summary.external_count}
          </p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Severity Distribution Pie Chart */}
        <div className="bg-slate-900/60 border border-slate-700 rounded-lg p-4">
          <h3 className="text-sm font-bold text-white uppercase mb-4 flex items-center gap-2">
            <PieChart className="w-4 h-4 text-red-400" />
            Open NCRs by Severity
          </h3>
          {severityData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <RechartsPie>
                <Pie
                  data={severityData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {severityData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }} />
              </RechartsPie>
            </ResponsiveContainer>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-slate-500">
              No open NCRs
            </div>
          )}
        </div>

        {/* Source Distribution */}
        <div className="bg-slate-900/60 border border-slate-700 rounded-lg p-4">
          <h3 className="text-sm font-bold text-white uppercase mb-4 flex items-center gap-2">
            <FileWarning className="w-4 h-4 text-cyan-400" />
            NCRs by Source
          </h3>
          <ResponsiveContainer width="100%" height={200}>
            <RechartsPie>
              <Pie
                data={sourceData}
                cx="50%"
                cy="50%"
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
                label={({ name, percent }) => `${name.split(' ')[0]} ${(percent * 100).toFixed(0)}%`}
              >
                {sourceData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }} />
            </RechartsPie>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Department Bar Chart */}
      <div className="bg-slate-900/60 border border-slate-700 rounded-lg p-4">
        <h3 className="text-sm font-bold text-white uppercase mb-4 flex items-center gap-2">
          <Building2 className="w-4 h-4 text-cyan-400" />
          NCRs by Department
        </h3>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={departmentData} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis type="number" stroke="#64748b" />
            <YAxis dataKey="name" type="category" stroke="#64748b" width={100} tick={{ fontSize: 11 }} />
            <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }} />
            <Legend />
            <Bar dataKey="open" name="Open" fill="#ef4444" stackId="a" />
            <Bar dataKey="closed" name="Closed" fill="#22c55e" stackId="a" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* 30 Day Trend */}
      <div className="bg-slate-900/60 border border-slate-700 rounded-lg p-4">
        <h3 className="text-sm font-bold text-white uppercase mb-4 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-cyan-400" />
          30-Day NCR Trend
        </h3>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={data.trend_30_days}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis dataKey="date" stroke="#64748b" tick={{ fontSize: 10 }} tickFormatter={(val) => val.slice(5)} />
            <YAxis stroke="#64748b" />
            <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }} />
            <Legend />
            <Line type="monotone" dataKey="raised" name="Raised" stroke="#ef4444" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="closed" name="Closed" stroke="#22c55e" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Overdue NCRs Table */}
      {data.overdue_ncrs.length > 0 && (
        <div className="bg-slate-900/60 border border-orange-500/30 rounded-lg p-4">
          <h3 className="text-sm font-bold text-orange-400 uppercase mb-4 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            Overdue NCRs ({data.overdue_count})
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left py-2 px-3 text-slate-400 uppercase text-xs">NCR #</th>
                  <th className="text-left py-2 px-3 text-slate-400 uppercase text-xs">Severity</th>
                  <th className="text-left py-2 px-3 text-slate-400 uppercase text-xs">Department</th>
                  <th className="text-left py-2 px-3 text-slate-400 uppercase text-xs">Responsible</th>
                  <th className="text-right py-2 px-3 text-slate-400 uppercase text-xs">Days Overdue</th>
                </tr>
              </thead>
              <tbody>
                {data.overdue_ncrs.map((ncr) => (
                  <tr key={ncr.ncr_number} className="border-b border-slate-800 hover:bg-slate-800/50">
                    <td className="py-2 px-3 font-mono text-white">{ncr.ncr_number}</td>
                    <td className="py-2 px-3">
                      <span className={`text-xs px-2 py-0.5 rounded font-bold ${
                        ncr.severity === 'Critical' ? 'bg-red-600 text-white' :
                        ncr.severity === 'Medium' ? 'bg-orange-500 text-white' : 'bg-yellow-500 text-black'
                      }`}>
                        {ncr.severity}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-slate-300">{ncr.department}</td>
                    <td className="py-2 px-3 text-slate-300">{ncr.responsible_person || '-'}</td>
                    <td className="py-2 px-3 text-right text-red-400 font-bold">{ncr.days_overdue}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent NCRs */}
      <div className="bg-slate-900/60 border border-slate-700 rounded-lg p-4">
        <h3 className="text-sm font-bold text-white uppercase mb-4 flex items-center gap-2">
          <Calendar className="w-4 h-4 text-cyan-400" />
          Recent NCRs
        </h3>
        <div className="space-y-2">
          {data.recent_ncrs.map((ncr) => (
            <div key={ncr.ncr_number} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg hover:bg-slate-800">
              <div className="flex items-center gap-4">
                <span className="font-mono text-white font-bold">{ncr.ncr_number}</span>
                <span className={`text-xs px-2 py-0.5 rounded font-bold ${
                  ncr.severity === 'Critical' ? 'bg-red-600 text-white' :
                  ncr.severity === 'Medium' ? 'bg-orange-500 text-white' : 'bg-yellow-500 text-black'
                }`}>
                  {ncr.severity}
                </span>
                <span className={`text-xs px-2 py-0.5 rounded ${
                  ncr.ncr_type === 'External' ? 'bg-purple-950 text-purple-400' : 'bg-blue-950 text-blue-400'
                }`}>
                  {ncr.ncr_type}
                </span>
                <span className="text-slate-400 text-sm">{ncr.department}</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-xs text-slate-500">{formatDate(ncr.date_raised)}</span>
                <span className={`text-xs px-2 py-0.5 rounded ${
                  ncr.status === 'Closed' ? 'bg-green-950 text-green-400' : 'bg-slate-700 text-slate-300'
                }`}>
                  {ncr.status.split(' / ')[0]}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NcrDashboard;
